/**
 * TrustGrid Project Selector Component
 * Displays current project with dropdown to switch projects
 */
import React, { useState, useEffect } from 'react';
import { Select, Spin } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { useUser } from '../../../contexts/UserContext';
import { listProjects } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const { Option } = Select;

function ProjectSelector() {
  const location = useLocation();
  const { selectedProjectId, selectedProject, selectProject } = useTrustGrid();
  const { user } = useUser();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Determine if "All Projects" option should be shown based on current page
  // Dashboard requires single project, so hide "All Projects" there
  const showAllProjectsOption = !location.pathname.includes('/dashboard');

  // Re-fetch projects whenever user changes (logout/login)
  useEffect(() => {
    if (user) {
      fetchProjects();
    } else {
      // Clear projects when user logs out
      setProjects([]);
    }
  }, [user?.id]); // Re-run when user ID changes

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await listProjects();
      const projectList = response.data.projects || [];
      setProjects(projectList);

      // Auto-select project with most recent scan ONLY if:
      // 1. No project currently selected
      // 2. User has projects available
      // 3. Selected project is not 'all'
      if (!selectedProjectId && projectList.length > 0 && selectedProjectId !== 'all') {
        const projectWithScan = projectList
          .filter(p => p.last_scan_at)
          .sort((a, b) => new Date(b.last_scan_at) - new Date(a.last_scan_at))[0];

        const defaultProject = projectWithScan || projectList[0];
        selectProject(defaultProject.id, defaultProject);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId) => {
    if (projectId === 'all') {
      // Set to 'all' string instead of null to distinguish from "no selection"
      selectProject('all', null);
    } else {
      const project = projects.find(p => p.id === projectId);
      selectProject(projectId, project);
    }
  };

  // Show loading only while fetching
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', marginBottom: '16px' }}>
        <Spin size="small" />
      </div>
    );
  }

  // If no projects, show empty state
  if (projects.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 0',
        marginBottom: '16px',
        color: '#888'
      }}>
        <ProjectOutlined style={{ color: '#8B3C46', fontSize: '16px' }} />
        <span>No projects available</span>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '8px 0',
      marginBottom: '16px'
    }}>
      <ProjectOutlined style={{ color: '#8B3C46', fontSize: '16px' }} />
      <span style={{ fontWeight: 500, color: '#17052B' }}>Project:</span>
      <Select
        value={selectedProjectId || (showAllProjectsOption ? 'all' : projects[0]?.id)}
        onChange={handleProjectChange}
        style={{ minWidth: 250 }}
        placeholder="Select a project"
      >
        {showAllProjectsOption && (
          <Option key="all" value="all">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>All Projects</span>
            </div>
          </Option>
        )}
        {projects.map(project => (
          <Option key={project.id} value={project.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{project.name}</span>
              {project.trust_score !== null && project.trust_score !== undefined && (
                <span style={{
                  marginLeft: '12px',
                  fontSize: '12px',
                  color: project.trust_score >= 70 ? '#2e7d32' : '#d32f2f',
                  fontWeight: 500
                }}>
                  {project.trust_score}%
                </span>
              )}
            </div>
          </Option>
        ))}
      </Select>
    </div>
  );
}

export default ProjectSelector;
